import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, Loader2, Merge } from 'lucide-react';
import ticketsService from '@/services/tickets.service';
import { transformApiTicketToFrontend } from '@/lib/ticketUtils';
import { formatDateTime } from '@/lib/format';
import { useTranslation } from 'react-i18next';

interface TicketListModalWithSelectionProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectMultiple: (ticketIds: string[]) => void;
  excludeTicketId?: string; // Current ticket ID to exclude from list
}

export default function TicketListModalWithSelection({
  isOpen,
  onClose,
  onSelectMultiple,
  excludeTicketId
}: TicketListModalWithSelectionProps) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInContent, setSearchInContent] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState<string[]>([]);

  // Reset selections when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedTickets([]);
      setSearchQuery('');
      setSearchResults([]);
    }
  }, [isOpen]);

  // Debounced search effect
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const response = await ticketsService.searchTickets({
          q: searchQuery,
          searchInContent: searchInContent,
          limit: 50
        });

        // Transform and filter out current ticket
        const transformedResults = response.data.tickets
          .map(transformApiTicketToFrontend)
          .filter(ticket => ticket.id !== excludeTicketId);

        setSearchResults(transformedResults);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, searchInContent, excludeTicketId]);

  const handleTicketToggle = (ticketId: string) => {
    setSelectedTickets(prev => {
      if (prev.includes(ticketId)) {
        return prev.filter(id => id !== ticketId);
      } else {
        return [...prev, ticketId];
      }
    });
  };

  const handleMerge = () => {
    if (selectedTickets.length > 0) {
      onSelectMultiple(selectedTickets);
      // Reset will happen via isOpen effect
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="h-5 w-5 text-orange-600" />
            {t('ticket:action.mergeTickets', 'Tickets zusammenführen')}
          </DialogTitle>
          <DialogDescription>
            Wählen Sie Tickets aus, die mit diesem Ticket zusammengeführt werden sollen
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input with Toggle */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder={t('common:x1.searchTicketPh', 'Ticket suchen (Nummer, Betreff, E-Mail...)')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                autoFocus
              />
            </div>

            {/* Search Options Toggle */}
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Switch
                id="modal-merge-search-in-content"
                checked={searchInContent}
                onCheckedChange={setSearchInContent}
                className="data-[state=checked]:bg-[#0F99FD]"
              />
              <label
                htmlFor="modal-merge-search-in-content"
                className="text-sm text-gray-700 cursor-pointer"
              >
                {t('common:x1.searchInContent', 'Im Inhalt suchen')}
              </label>
            </div>
          </div>

          {/* Selected Count */}
          {selectedTickets.length > 0 && (
            <div className="rounded-lg bg-orange-50 border border-orange-200 px-3 py-2">
              <p className="text-sm font-medium text-orange-800">
                {selectedTickets.length} Ticket{selectedTickets.length > 1 ? 's' : ''} ausgewählt
              </p>
            </div>
          )}

          {/* Search Results */}
          <div className="min-h-[300px] max-h-[400px] overflow-y-auto border rounded-md">
            {!searchQuery.trim() ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Search className="h-12 w-12 mb-3" />
                <p className="text-sm">{t('common:x1.enterSearchTerm', 'Geben Sie einen Suchbegriff ein')}</p>
              </div>
            ) : isSearching ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500">{t('common:x1.searching', 'Suche läuft...')}</span>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Search className="h-12 w-12 mb-3" />
                <p className="text-sm">{t('common:x1.noTicketsFound', 'Keine Tickets gefunden')}</p>
              </div>
            ) : (
              <div className="divide-y">
                {searchResults.map((ticket) => {
                  const isSelected = selectedTickets.includes(ticket.id);
                  return (
                    <div
                      key={ticket.id}
                      className={`cursor-pointer border-b border-slate-200/60 p-3 last:border-b-0 transition-colors ${
                        isSelected ? 'bg-orange-50' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => handleTicketToggle(ticket.id)}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => handleTicketToggle(ticket.id)}
                          className="mt-1"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="mb-1 flex items-center justify-between">
                            <span className="text-sm font-semibold text-slate-800 truncate">
                              {ticket.subject}
                            </span>
                            <span className="text-xs text-slate-500 ml-2 flex-shrink-0">
                              {ticket.ticketNumber}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span className="truncate">{ticket.requester?.name || 'Unknown'}</span>
                            <span className="text-slate-300">•</span>
                            <span className="truncate">{ticket.organization?.name || 'N/A'}</span>
                            <span className="text-slate-300">•</span>
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                                ticket.status === 'open'
                                  ? 'bg-green-100 text-green-700'
                                  : ticket.status === 'pending'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : ticket.status === 'closed'
                                  ? 'bg-slate-100 text-slate-600'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                            >
                              {ticket.status === 'open' ? 'Offen' :
                               ticket.status === 'pending' ? 'Wartend' :
                               ticket.status === 'closed' ? 'Geschlossen' : 'Neu'}
                            </span>
                          </div>
                          {ticket.updatedAt && (
                            <div className="text-[11px] text-slate-400 mt-1">
                              {t('common:x1.ticketProcessing', 'Ticketbearbeitung:')} {formatDateTime(ticket.updatedAt)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between items-center gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common:action.cancel', 'Abbrechen')}
          </Button>
          <Button
            onClick={handleMerge}
            disabled={selectedTickets.length === 0}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            <Merge className="h-4 w-4 mr-2" />
            {selectedTickets.length > 0
              ? `${selectedTickets.length} Ticket${selectedTickets.length > 1 ? 's' : ''} zusammenführen`
              : 'Tickets auswählen'
            }
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
